import { useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/shared/components/ui/tabs';
import { cn } from '@/shared/utils/cn';
import { useEngagementMetricsFilters } from '../../hooks/useEngagementMetricsFilters';
import { useAnalyticsRefresh } from '../../hooks/useAnalyticsRefresh';
import { EngagementMetricsControlBar } from './EngagementMetricsControlBar';
import { EngagementMetricsOverviewTab } from './tabs/EngagementMetricsOverviewTab';
import { EngagementMetricsLogTab } from './tabs/EngagementMetricsLogTab';
import { EngagementMetricsBillingTab } from './tabs/EngagementMetricsBillingTab';
import { EngagementMetricsReferralFeedbackTab } from './tabs/EngagementMetricsReferralFeedbackTab';
import { EngagementMetricsReferralsTab } from './tabs/EngagementMetricsReferralsTab';
import { ReferralRowActions } from './referrals/ReferralRowActions';
import { RegisterReferralButton } from './referrals/RegisterReferralButton';
import { ReferralsExportButton } from './referrals/ReferralsExportButton';
import { BulkUploadReferralsButton } from './referrals/BulkUploadReferralsButton';
import { BulkUploadSurface } from '../manual-referrals/bulk-upload/BulkUploadSurface';
import type { EngagementMetricsSubTab } from '../../types/engagement-metrics.types';

const SUB_TABS: { value: EngagementMetricsSubTab; label: string }[] = [
  { value: 'overview', label: 'Overview' },
  { value: 'log', label: 'Engagements Log' },
  { value: 'billing', label: 'Billing' },
  { value: 'referral-feedback', label: 'Referral Feedback' },
  { value: 'referrals', label: 'Referrals' },
];

export const EngagementMetricsDashboard = () => {
  const [activeTab, setActiveTab] = useState<EngagementMetricsSubTab>('overview');
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const filters = useEngagementMetricsFilters();
  const refresh = useAnalyticsRefresh();

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-gray-200 bg-white px-4 py-4 lg:px-6 lg:py-6">
        <h1 className="text-xl lg:text-2xl font-bold text-gray-900">
          Engagement Metrics
        </h1>
        <p className="text-sm lg:text-base text-gray-600 mt-1">
          Every qualifying provider engagement — for billing service providers
          and marketing to partners &amp; advisors.
        </p>
      </div>

      {/* The Referrals tab is a live table: date bar and MV refresh don't apply (117/04). */}
      {activeTab !== 'referrals' && (
        <EngagementMetricsControlBar
          dateRange={filters.dateRange}
          onDateRangeChange={filters.setDateRange}
          lastRefreshed={refresh.oldestRefresh}
          onRefresh={refresh.triggerRefresh}
          isRefreshing={refresh.isRefreshing}
          canRefresh={refresh.canRefresh}
        />
      )}

      <div className="px-4 lg:px-6">
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as EngagementMetricsSubTab)}
        >
          <TabsList className="w-full justify-start border-b bg-transparent p-0 h-auto rounded-none">
            {SUB_TABS.map(({ value, label }) => (
              <TabsTrigger
                key={value}
                value={value}
                className="flex items-center gap-2 px-4 py-3 rounded-none data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#15543e] data-[state=active]:text-[#15543e] data-[state=active]:font-bold text-gray-500"
              >
                <span>{label}</span>
                <span
                  className={cn(
                    'inline-flex min-w-[1.25rem] justify-center rounded-full px-1.5 py-0.5 text-[11px] font-semibold',
                    activeTab === value
                      ? 'bg-[#e4ede8] text-[#2c5c47]'
                      : 'bg-gray-100 text-gray-500',
                  )}
                >
                  0
                </span>
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="overview">
            <EngagementMetricsOverviewTab
              queryParams={filters.queryParams}
              isActive={activeTab === 'overview'}
            />
          </TabsContent>

          <TabsContent value="log">
            <EngagementMetricsLogTab
              queryParams={filters.queryParams}
              isActive={activeTab === 'log'}
            />
          </TabsContent>

          <TabsContent value="billing">
            <EngagementMetricsBillingTab />
          </TabsContent>

          <TabsContent value="referral-feedback">
            <EngagementMetricsReferralFeedbackTab
              isActive={activeTab === 'referral-feedback'}
            />
          </TabsContent>

          <TabsContent value="referrals">
            {isBulkUploadOpen ? (
              <BulkUploadSurface onBack={() => setIsBulkUploadOpen(false)} />
            ) : (
              <EngagementMetricsReferralsTab
                isActive={activeTab === 'referrals'}
                renderRowActions={(row) => <ReferralRowActions row={row} />}
                renderHeaderActions={(params, canManage) => (
                  <>
                    <ReferralsExportButton params={params} />
                    {canManage && <BulkUploadReferralsButton onClick={() => setIsBulkUploadOpen(true)} />}
                    {canManage && <RegisterReferralButton />}
                  </>
                )}
              />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
